import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
});

// Define a more specific error type
interface SupabaseErrorResponse {
  code?: string;
  message: string;
  details?: string;
}

interface SupabaseOperationResult<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  details?: string;
}

// Enhanced error handling utility
export const handleSupabaseError = <T = unknown>(
  error: SupabaseErrorResponse,
  context: string = 'Operation'
): SupabaseOperationResult<T> => {
  console.error(`Supabase ${context} Error:`, error);
  
  // Detailed error logging
  if (error.code === '42501') {
    console.error('Row-level security policy violation. Check user permissions.');
  }
  
  // Specific error type handling
  switch (error.code) {
    case '42501':
      return {
        success: false,
        message: 'Permission denied. You do not have access to perform this action.',
        details: error.message
      } as SupabaseOperationResult<T>;
    case '23505':
      return {
        success: false,
        message: 'Unique constraint violation. This record may already exist.',
        details: error.message
      } as SupabaseOperationResult<T>;
    default:
      return {
        success: false,
        message: `${context} failed`,
        details: error.message
      } as SupabaseOperationResult<T>;
  }
};

// Wrapper for Supabase operations with enhanced error handling
export const safeSupabaseCall = async <T = unknown>(
  operation: () => Promise<T>,
  context: string = 'Operation'
): Promise<SupabaseOperationResult<T>> => {
  try {
    const result = await operation();
    return { success: true, data: result };
  } catch (error) {
    return handleSupabaseError<T>(error as SupabaseErrorResponse, context);
  }
};
